import { useEffect, useRef, useState } from "react";
import { forecastLocalDate, nextForecastDateChange } from "@/lib/forecast-date-rollover";

/** One refresh per observed local date change, including a tab waking after midnight. */
export function useForecastDateRollover(
  identity: string,
  timezone: string | undefined,
  enabled: boolean,
  onDateChange: () => void,
  cachedBaseDate?: string,
) {
  const callback = useRef(onDateChange);
  callback.current = onDateChange;
  const [, setRevision] = useState(0);
  const date = forecastLocalDate(timezone);

  useEffect(() => {
    if (!enabled || !timezone) return;
    let observedDate = cachedBaseDate ?? forecastLocalDate(timezone);
    if (!observedDate) return;
    let timer: ReturnType<typeof setTimeout>;
    const check = () => {
      clearTimeout(timer);
      const currentDate = forecastLocalDate(timezone);
      if (currentDate && currentDate !== observedDate) {
        // Advance before requesting; a failure or repeated focus must not retry automatically.
        observedDate = currentDate;
        setRevision(value => value + 1);
        callback.current();
      }
      timer = setTimeout(check, Math.max(1, nextForecastDateChange(Date.now(), timezone) - Date.now()));
    };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    // A newly mounted, fresh-by-age cache can still belong to yesterday.
    check();
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [identity, timezone, enabled, cachedBaseDate]);

  return date;
}
